import { StackItem, StackLayout } from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import { useNavigate } from 'react-router-dom';
import { ErrorState } from '@leagueplatform/web-common-components';
import { ROUTE_PATHS } from 'common/constants/constants';
import { useIsBelowTablet } from 'hooks/use-is-below-tablet/use-is-below-tablet.hooks';
import { PAGE_NAVIGATION_HEIGHT } from 'common/constants/ui';
import ERROR_IMAGE from 'assets/generic-error-illustration.png';
import {
  type ErrorPageProps,
  genericErrorProps,
  pageNotFoundProps,
} from 'common/constants/error-page';
import { type LocalesKey } from 'common/types/types';

export const ErrorPage = ({
  imageSrc,
  bodyText,
  headingLocaleKey,
  buttonLocaleKey,
  hasGoToHomepage,
  isFullPage,
}: ErrorPageProps) => {
  const navigate = useNavigate();

  const { formatMessage } = useIntl();
  const isBelowTablet = useIsBelowTablet();
  return (
    <StackLayout
      verticalAlignment="center"
      horizontalAlignment="center"
      css={{
        height: isFullPage
          ? '100vh'
          : `calc(100vh - ${PAGE_NAVIGATION_HEIGHT}px)`,
        ...(isFullPage && {
          backgroundColor: '$surfaceBackgroundPrimary',
        }),
      }}
    >
      <StackItem
        css={{
          ...(isBelowTablet && { height: '100%' }),
          minWidth: isBelowTablet ? '100vw' : 708,
          background: '$surfaceCardSecondary',
          boxShadow: '$card',
          padding: isBelowTablet ? '$oneAndHalf' : '$twoAndHalf',
          '& img': {
            width: 176,
          },
        }}
      >
        <ErrorState
          css={{
            marginBlockStart: '$one',
            '.error-state-button-wrapper': {
              display: 'block',
              width: '100%',
            },
            '.error-state-button': {
              ...(isBelowTablet ? { width: '100%' } : { minWidth: 320 }),
              justifyContent: 'center',
            },
          }}
          error={null}
          imageSrc={imageSrc || ERROR_IMAGE}
          headingString={formatMessage({ id: headingLocaleKey })}
          {...(bodyText && {
            bodyString: bodyText.startsWith('STR_')
              ? formatMessage({ id: bodyText as LocalesKey })
              : bodyText,
          })}
          {...(buttonLocaleKey
            ? {
                buttonText: formatMessage({ id: buttonLocaleKey }),
              }
            : { hideButton: true })}
          {...(hasGoToHomepage && {
            onButtonClick: () => navigate(ROUTE_PATHS.HOME),
          })}
        />
      </StackItem>
    </StackLayout>
  );
};

export const GenericErrorPage = () => <ErrorPage {...genericErrorProps} />;
export const PageNotFoundErrorPage = () => <ErrorPage {...pageNotFoundProps} />;
